import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  addLocalDays,
  fetchScheduleOverviewDayDetail,
  fetchScheduleOverviewIsoWeek,
  isAdminRole,
  isFleetOwnerRole,
  parseUserRole,
  startOfIsoWeek,
  supabase,
  toLocalDateString,
  useAuthUser,
  useFleetOperator,
  type ScheduleDaySummary,
  type ScheduleOverviewDayDetail,
  type ScheduleOverviewRange,
} from '@ubus/shared';
import AdminSectionPage from '../components/AdminSectionPage';
import {
  OverviewDayOccupancy,
  OverviewUnscheduledList,
} from '../components/OverviewDayOccupancy';
import OverviewTripPeekDrawer from '../components/OverviewTripPeekDrawer';

const WEEKDAY_KEYS = [
  'weekdayMon',
  'weekdayTue',
  'weekdayWed',
  'weekdayThu',
  'weekdayFri',
  'weekdaySat',
  'weekdaySun',
] as const;

function heatColor(ratio: number): string {
  if (ratio <= 0) return 'var(--ant-color-fill-secondary, #f0f0f0)';
  if (ratio < 0.35) return '#b7eb8f';
  if (ratio < 0.7) return '#ffe58f';
  return '#ffccc7';
}

export default function ScheduleOverview() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthUser();
  const role = parseUserRole(user);
  const isOwner = isFleetOwnerRole(role);
  const isAdmin = isAdminRole(role);
  const { operatorId, loading: operatorLoading } = useFleetOperator();

  const today = toLocalDateString();
  const [anchorDate, setAnchorDate] = useState(() => startOfIsoWeek(today));
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<ScheduleOverviewRange | null>(null);
  const [vehicleIds, setVehicleIds] = useState<string[] | null>(null);
  const [dayDetail, setDayDetail] = useState<ScheduleOverviewDayDetail | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [peekTripId, setPeekTripId] = useState<string | null>(null);

  // Owner: resolve fleet vehicle ids
  useEffect(() => {
    if (!isOwner) {
      setVehicleIds(null);
      return;
    }
    if (operatorLoading) return;
    if (!operatorId) {
      setVehicleIds([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('id')
        .eq('fleet_operator_id', operatorId)
        .eq('status', 'active');
      if (cancelled) return;
      if (error) {
        message.error(error.message);
        setVehicleIds([]);
        return;
      }
      setVehicleIds((data ?? []).map((v) => v.id as string));
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner, operatorId, operatorLoading]);

  const fleetReady = !isOwner || (!operatorLoading && vehicleIds !== null);

  const load = useCallback(async () => {
    if (!fleetReady) return;
    setLoading(true);
    try {
      const data = await fetchScheduleOverviewIsoWeek({
        anchorDate,
        userId: user?.id ?? null,
        vehicleIds: isOwner ? vehicleIds : null,
        includeUnscheduledJobs: isAdmin,
      });
      setOverview(data);
      setSelectedDate((prev) => {
        if (prev >= data.fromDate && prev <= data.toDate) return prev;
        if (today >= data.fromDate && today <= data.toDate) return today;
        return data.fromDate;
      });
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : t('scheduleOverview.loadFailed')
      );
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [anchorDate, fleetReady, isAdmin, isOwner, t, today, user?.id, vehicleIds]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!fleetReady || !selectedDate) return;
    let cancelled = false;
    setDayLoading(true);
    void (async () => {
      try {
        const detail = await fetchScheduleOverviewDayDetail({
          date: selectedDate,
          vehicleIds: isOwner ? vehicleIds : null,
          includeUnscheduledJobs: isAdmin,
        });
        if (!cancelled) setDayDetail(detail);
      } catch (err) {
        if (!cancelled) {
          message.error(
            err instanceof Error ? err.message : t('scheduleOverview.loadFailed')
          );
          setDayDetail(null);
        }
      } finally {
        if (!cancelled) setDayLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fleetReady, isAdmin, isOwner, selectedDate, t, vehicleIds]);

  const selected = useMemo(
    () => overview?.days.find((d) => d.date === selectedDate) ?? null,
    [overview, selectedDate]
  );

  const weekLabel = useMemo(() => {
    const start = overview?.fromDate ?? anchorDate;
    try {
      const [y, m, d] = start.split('-').map(Number);
      const fmt = new Intl.DateTimeFormat(i18n.language.startsWith('zh') ? 'zh-CN' : 'en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return t('scheduleOverview.weekOf', {
        start: fmt.format(new Date(y, m - 1, d)),
      });
    } catch {
      return t('scheduleOverview.weekOf', { start });
    }
  }, [anchorDate, i18n.language, overview?.fromDate, t]);

  const goThisWeek = () => setAnchorDate(startOfIsoWeek(today));
  const goPrev = () => setAnchorDate((d) => addLocalDays(startOfIsoWeek(d), -7));
  const goNext = () => setAnchorDate((d) => addLocalDays(startOfIsoWeek(d), 7));

  const openOps = (date: string) => {
    navigate(`/trips?date=${encodeURIComponent(date)}`);
  };

  const ctaLabel = isOwner
    ? t('scheduleOverview.openOwnerSchedule')
    : t('scheduleOverview.openTrips');

  const pageLoading = (loading && !overview) || (isOwner && !fleetReady);

  return (
    <AdminSectionPage
      title={t('scheduleOverview.title')}
      subtitle={
        isOwner
          ? t('scheduleOverview.subtitleOwner')
          : t('scheduleOverview.subtitle')
      }
      loading={pageLoading}
      bare
      widthMode="fluid"
      extra={
        <Space wrap>
          <Button icon={<LeftOutlined />} onClick={goPrev} aria-label={t('scheduleOverview.prevWeek')} />
          <Button onClick={goThisWeek}>{t('scheduleOverview.thisWeek')}</Button>
          <Button icon={<RightOutlined />} onClick={goNext} aria-label={t('scheduleOverview.nextWeek')} />
          <Typography.Text type="secondary">{weekLabel}</Typography.Text>
        </Space>
      }
    >
      {overview ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 16,
              marginBottom: 20,
            }}
          >
            {isAdmin ? (
              <Statistic
                title={t('scheduleOverview.metricUnscheduled')}
                value={overview.totals.unscheduledJobs}
              />
            ) : null}
            <Statistic title={t('scheduleOverview.metricPublished')} value={overview.totals.publishedTrips} />
            {isAdmin ? (
              <Statistic title={t('scheduleOverview.metricDrafts')} value={overview.totals.draftDays} />
            ) : null}
            <Statistic title={t('scheduleOverview.metricFleet')} value={overview.activeVehicleCount} />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 8,
              marginBottom: 20,
            }}
          >
            {overview.days.map((day, idx) => (
              <DayCell
                key={day.date}
                day={day}
                weekdayLabel={t(`scheduleOverview.${WEEKDAY_KEYS[idx]}`)}
                selected={day.date === selectedDate}
                isToday={day.date === today}
                showUnscheduled={isAdmin}
                onSelect={() => setSelectedDate(day.date)}
                onOpen={() => openOps(day.date)}
                tUnscheduled={(n) => t('scheduleOverview.unscheduled', { count: n })}
                tPublished={(n) => t('scheduleOverview.published', { count: n })}
                tDraft={t('scheduleOverview.draft')}
                tNoActivity={t('scheduleOverview.noActivity')}
              />
            ))}
          </div>

          {selected ? (
            <div
              style={{
                padding: '16px 20px',
                background: 'var(--ant-color-fill-quaternary, #fafafa)',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                  justifyContent: 'space-between',
                }}
              >
                <Space wrap size="middle">
                  <Typography.Text strong>
                    {t('scheduleOverview.selected', { date: selected.date })}
                  </Typography.Text>
                  {isAdmin ? (
                    <Tag color={selected.unscheduledJobCount > 0 ? 'orange' : 'default'}>
                      {t('scheduleOverview.unscheduled', { count: selected.unscheduledJobCount })}
                    </Tag>
                  ) : null}
                  <Tag color="blue">
                    {t('scheduleOverview.published', { count: selected.publishedTripCount })}
                  </Tag>
                  {isAdmin && selected.hasLocalDraft ? (
                    <Tag color="purple">{t('scheduleOverview.draft')}</Tag>
                  ) : null}
                </Space>
                <Button
                  type="primary"
                  icon={<CalendarOutlined />}
                  onClick={() => openOps(selected.date)}
                >
                  {ctaLabel}
                </Button>
              </div>

              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {t('scheduleOverview.occupancyTitle')}
                </Typography.Text>
                {dayLoading ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin />
                  </div>
                ) : dayDetail ? (
                  <OverviewDayOccupancy
                    detail={dayDetail}
                    selectedTripId={peekTripId}
                    onTripClick={(id) => setPeekTripId(id)}
                  />
                ) : null}
              </div>

              {isAdmin && dayDetail ? (
                <div>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {t('scheduleOverview.unscheduledTitle')}
                  </Typography.Text>
                  <OverviewUnscheduledList
                    jobs={dayDetail.unscheduledJobs}
                    onOpenSchedule={() => openOps(selected.date)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <Alert type="error" showIcon message={t('scheduleOverview.loadFailed')} />
      )}

      <OverviewTripPeekDrawer
        tripId={peekTripId}
        open={Boolean(peekTripId)}
        onClose={() => setPeekTripId(null)}
        isOwner={isOwner}
        onOpenInTrips={(trip) => {
          setPeekTripId(null);
          navigate(`/trips?date=${encodeURIComponent(trip.date)}&trip=${encodeURIComponent(trip.id)}`);
        }}
        onOpenOwnerSchedule={(date, tripId) => {
          setPeekTripId(null);
          navigate(
            `/trips?date=${encodeURIComponent(date)}&trip=${encodeURIComponent(tripId)}`
          );
        }}
      />
    </AdminSectionPage>
  );
}

function DayCell(props: {
  day: ScheduleDaySummary;
  weekdayLabel: string;
  selected: boolean;
  isToday: boolean;
  showUnscheduled: boolean;
  onSelect: () => void;
  onOpen: () => void;
  tUnscheduled: (n: number) => string;
  tPublished: (n: number) => string;
  tDraft: string;
  tNoActivity: string;
}) {
  const { day } = props;
  const dayNum = day.date.slice(8);
  const quiet =
    (!props.showUnscheduled || day.unscheduledJobCount === 0) &&
    day.publishedTripCount === 0 &&
    !day.hasLocalDraft;

  return (
    <button
      type="button"
      onClick={props.onSelect}
      onDoubleClick={props.onOpen}
      style={{
        textAlign: 'left',
        border: props.selected
          ? '2px solid var(--ant-color-primary, #1677ff)'
          : '1px solid var(--ant-color-border, #d9d9d9)',
        borderRadius: 8,
        padding: 10,
        minHeight: 128,
        background: '#fff',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {props.weekdayLabel}
        </Typography.Text>
        <Typography.Text strong style={{ fontSize: 18 }}>
          {dayNum}
          {props.isToday ? (
            <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
              ·
            </Typography.Text>
          ) : null}
        </Typography.Text>
      </div>

      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: heatColor(day.occupancyRatio),
          width: `${Math.max(12, Math.round(day.occupancyRatio * 100))}%`,
          maxWidth: '100%',
          alignSelf: 'stretch',
        }}
        title={`${Math.round(day.occupancyRatio * 100)}%`}
      />

      {quiet ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {props.tNoActivity}
        </Typography.Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {props.showUnscheduled && day.unscheduledJobCount > 0 ? (
            <Typography.Text style={{ fontSize: 12, color: '#d46b08' }}>
              {props.tUnscheduled(day.unscheduledJobCount)}
            </Typography.Text>
          ) : null}
          {day.publishedTripCount > 0 ? (
            <Typography.Text style={{ fontSize: 12 }}>
              {props.tPublished(day.publishedTripCount)}
            </Typography.Text>
          ) : null}
          {props.showUnscheduled && day.hasLocalDraft ? (
            <Tag color="purple" style={{ marginInlineEnd: 0, width: 'fit-content' }}>
              {props.tDraft}
            </Tag>
          ) : null}
        </div>
      )}
    </button>
  );
}
